import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import DefaultSelectV2 from '../DefaultSelectV2';
import MultiSelectV2 from '../MultiSelectV2';
import BaseService from 'services/BaseService';
import { filterOptionsByParentId } from '../../../utils/optionSelectFilter';

const mapApiOption = (option, multiSelect) =>
  multiSelect
    ? { value: option.id, label: option.value, parentId: option.parentId }
    : { id: option.id, nome: option.value, parentId: option.parentId };

const OptionSelectV2 = ({
  onSelect,
  value,
  isRequired,
  error,
  helperText,
  disabled,
  codigo,
  multiSelect,
  label,
  parentId,
  parentIds,
}) => {
  const [allOptions, setAllOptions] = useState([]);

  const visibleOptions = useMemo(
    () => filterOptionsByParentId(allOptions, { parentId, parentIds }),
    [allOptions, parentId, parentIds],
  );

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const response = await BaseService.get({ url: `options/${codigo}` });
        setAllOptions((response.data || []).map((option) => mapApiOption(option, multiSelect)));
      } catch (e) {
        console.error(e);
      }
    };

    loadOptions();
  }, [codigo, multiSelect]);

  useEffect(() => {
    // Wait for catalog to load before invalidating value (avoids clearing
    // a valid server value on first mount while fetch is in flight).
    if (allOptions.length === 0) return;

    if (multiSelect) {
      if (!Array.isArray(value) || value.length === 0) return;
      const validIds = value.filter((id) =>
        visibleOptions.some((option) => String(option.value) === String(id)),
      );
      if (validIds.length !== value.length) {
        onSelect(validIds.length > 0 ? validIds : null);
      }
      return;
    }

    if (
      value != null &&
      value !== '' &&
      !visibleOptions.some((option) => String(option.id) === String(value))
    ) {
      onSelect(null, '');
    }
  }, [allOptions.length, visibleOptions, value, multiSelect, onSelect]);

  if (multiSelect) {
    const selectedOptions =
      visibleOptions.length > 0 && Array.isArray(value) && value.length > 0
        ? visibleOptions.filter((option) => value.some((id) => String(id) === String(option.value)))
        : [];

    return (
      <MultiSelectV2
        label={label}
        disabled={disabled}
        onChange={(values) => {
          onSelect(values ? values.map((v) => v.value) : null);
        }}
        defaultValue={selectedOptions}
        options={visibleOptions}
      />
    );
  }

  const handleSelect = (event) => {
    const id = event.target.value;
    const opt = visibleOptions.find((option) => String(option.id) === String(id));
    onSelect(id, opt ? opt.nome : '');
  };

  return (
    <DefaultSelectV2
      error={error}
      disabled={disabled}
      helperText={helperText}
      isRequired={isRequired}
      label={label}
      onSelect={handleSelect}
      optionLabel="nome"
      options={visibleOptions}
      value={value}
    />
  );
};

OptionSelectV2.propTypes = {
  onSelect: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isRequired: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  codigo: PropTypes.string.isRequired,
  multiSelect: PropTypes.bool,
  label: PropTypes.string,
  parentId: PropTypes.string,
  parentIds: PropTypes.arrayOf(PropTypes.string),
};

OptionSelectV2.defaultProps = {
  onSelect: () => {},
  value: null,
  isRequired: false,
  error: false,
  helperText: undefined,
  disabled: false,
  multiSelect: false,
  label: undefined,
  parentId: undefined,
  parentIds: undefined,
};

export default OptionSelectV2;
